package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayClosedException
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.NoPayRatesException
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase

/**
 * SH-128: an open shift on a past day (or today) had nobody assigned, so its labour cost was 0.
 * The manager records who worked it ([workerId] as `actualUserId`; the planned times become the
 * actual ones when none were clocked) or that nobody did (null: `notWorked`, no more asking).
 * The shift is repriced for that person and the day's record recomputed and saved, so the day's
 * and the month's actual labour cost follow. A closed (locked) day is final ([DayClosedException]).
 */
class RecordShiftWorkerUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    /**
     * The shift as recorded, the day's record after it (null if it could not be recomputed), and
     * whether the person has no pay rates at the workplace (their cost is then not counted).
     */
    data class Outcome(val shift: Shift, val record: DailyRecord?, val missingPayRates: Boolean = false)

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        shiftId: String,
        workerId: String?,
        editorUserId: String
    ): Result<Outcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().firstOrNull { it.date == shift.date }
        if (stored?.isLocked == true) throw DayClosedException(shift.date)

        shiftRepository.recordWorker(shiftId, workerId, editorUserId).getOrThrow()
        // Worked as planned unless times were recorded: the cost needs the hours.
        val recorded = if (workerId != null && !shift.hasActualTime) {
            shiftRepository.updateActualTime(shiftId, shift.plannedTime, editorUserId).getOrThrow()
            shift.withPlannedAsActual()
        } else shift
        val updated = recorded.copy(actualUserId = workerId, notWorked = workerId == null)

        // Priced for the person who worked it (a financial manager's pay rates), or its snapshot removed.
        val pricing = calculateShiftCost?.invoke(shiftId)
        // No pay rates for the workplace, or no pay data at all: the cost cannot be counted.
        val missingRates = pricing?.exceptionOrNull().let { it is NoPayRatesException || it is NotFoundException }
        val record = calculateDailyRecordUseCase.recompute(workplaceId, rosterId, shift.date) { it }.getOrNull()
        record?.let { financialRepository.saveDailyRecord(it).getOrThrow() }
        Outcome(updated, record, missingRates)
    }
}
